import styled, { css } from 'styled-components';

import { TONES } from '@/nova/components/Callout/Callout.styles';
import { Text } from '@/nova/components/Text/Text';
import { COLORS, MOBILE, SPACE } from '@/nova/theme/tokens';

import { HeaderPill } from '../AssetStats/StatsPill.styles';

/* "Links to this page" (a sheet opened from the composer's header): the page's own tracking link, then the ads'
   tracking links routed through the page (each opens its asset). Plus the made-for-an-asset bar over the composer. */

export const LinksToggleLabel = styled(Text).attrs({ tag: 'span' as const })``;

/** The count alone, on a phone only (the label carries it on a wide screen). */
export const LinksToggleCount = styled(Text).attrs({ tag: 'span' as const })`
  display: none;
`;

/** The header's "Links" pill, next to the version picker: the video composer's Stats pill. On a phone it keeps the
 *  count beside the glyph (the word goes), so it is not the Stats pill's fixed round button there. */
export const LinksToggle = styled(HeaderPill)`
  ${MOBILE} {
    && {
      width: auto;
      min-width: ${SPACE[40]};
      gap: ${SPACE[4]};
      padding: ${SPACE[0]} ${SPACE[12]};
    }

    && > ${LinksToggleLabel} {
      display: none;
    }

    && > ${LinksToggleCount} {
      display: inline;
    }
  }
`;

/** The sheet's sections, one under another. */
export const SheetBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[20]};
  min-width: 0;
`;

/** The links, one row each. */
export const LinkList = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid ${COLORS.hairline};
  border-radius: 12px;
  overflow: hidden;
`;

export const LinkRow = styled.li`
  display: flex;
  align-items: center;
  gap: ${SPACE[12]};
  min-width: 0;
  padding: ${SPACE[8]} ${SPACE[12]};
  background: ${COLORS.surfaceFaint};

  & + & {
    border-top: 1px solid ${COLORS.hairline};
  }
`;

export const LinkCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[2]};
  flex: 1 1 auto;
  min-width: 0;
`;

export const LinkKind = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${COLORS.textFaint};
`;

export const LinkName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** A deep link to an asset's composer (a real address, opened in the app on a plain click). */
export const AssetLink = styled.a`
  min-width: 0;
  color: inherit;
  text-decoration: underline;
  text-decoration-color: ${COLORS.hairline};
  text-underline-offset: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover,
  &:focus-visible {
    color: ${COLORS.accent};
    text-decoration-color: currentColor;
  }
`;

/** A link row's name as a deep link to its asset. */
export const LinkNameLink = styled(AssetLink)`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const LinkActions = styled.div`
  display: inline-flex;
  flex: 0 0 auto;
`;

/** The read failed: why, and a retry beside it. */
export const ErrorLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SPACE[8]};
`;

/* ── Made for an asset ── */

/** The bar's height on a wide sheet (a Button's fixed 48px, the bar's 4px padding above and below, and its border),
 *  the same with or without a button, taken out of the split's stage below it (with the sheet body's gap) so the
 *  sheet never scrolls. Layout, not a timing. */
export const LINK_FOR_BAR_PX = 58;
/** The sheet body's gap between the bar and the split (Sheet's own body gap). Layout. */
const SHEET_BODY_GAP_PX = 16;

export type LinkForTone = 'info' | 'success' | 'danger';

/** A slim one-line strip over the composer: the asset the page is made for, then the outcome of pointing its link
 *  here. One line on a wide sheet (the copy ellipsised), wrapping on a phone. */
export const LinkForBar = styled.div<{ $tone: LinkForTone }>`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  min-width: 0;
  min-height: ${LINK_FOR_BAR_PX}px;
  padding: ${SPACE[4]} ${SPACE[4]} ${SPACE[4]} ${SPACE[12]};
  border-radius: 12px;
  border: 1px solid ${(p) => TONES[p.$tone].border};
  background: ${(p) => TONES[p.$tone].bg};
  color: ${(p) => TONES[p.$tone].fg};

  ${MOBILE} {
    flex-wrap: wrap;
    padding: ${SPACE[8]} ${SPACE[12]};
  }
`;

/** "Made for", the asset name(s) and the note, on one line on a wide sheet (the names and the note ellipsised). */
export const LinkForCopy = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  gap: ${SPACE[8]};
  min-width: 0;
  overflow: hidden;
  font-size: 13px;

  ${MOBILE} {
    flex-wrap: wrap;
    row-gap: ${SPACE[2]};
  }
`;

export const LinkForLabel = styled(Text).attrs({ tag: 'span' as const })`
  flex: 0 0 auto;
  font-weight: 600;
  color: ${COLORS.textMuted};
`;

/** The asset name(s), each a deep link to its composer. */
export const LinkForAssets = styled.span`
  display: inline-flex;
  flex: 0 1 auto;
  gap: ${SPACE[8]};
  min-width: 0;
  overflow: hidden;
  font-weight: 700;
  color: ${COLORS.text};

  ${MOBILE} {
    flex-wrap: wrap;
  }
`;

export const LinkForNote = styled(Text).attrs({ tag: 'span' as const })`
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 500;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${MOBILE} {
    flex-basis: 100%;
    white-space: normal;
  }
`;

export const LinkForActions = styled.div`
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: ${SPACE[4]};
`;

/** Holds the split without a box of its own. Under the bar, the bar's height and the body's gap come out of the
 *  split's stage (a phone's split has no fixed height); doubled so it outranks the split's own height. */
export const SplitStage = styled.div<{ $underBar: boolean }>`
  display: contents;

  ${(p) =>
    p.$underBar &&
    css`
      && > * {
        height: calc(var(--sheet-stage-height, 100dvh) - 200px - ${LINK_FOR_BAR_PX + SHEET_BODY_GAP_PX}px);
      }

      ${MOBILE} {
        && > * {
          height: auto;
        }
      }
    `}
`;
